import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useRouteMatch} from 'react-router-dom'
import HeaderV1 from 'pages/HealthCenter/Report/Elements/HeaderV1'
import BrainFunctionalConnectivityV1 from 'pages/HealthCenter/ReportV1/BrainFunctionalConnectivity'
import useLanguage from 'features/languageReport/useLanguage'
import useEegSummaryReport from 'features/analysis/useEegSummaryReport'
import {fetchPatientApi} from 'api/patientApi'
import {GenderValue, toGenderString} from 'constants/CommonConstant'
import useStyles from 'pages/Analysis/EEGSummary/Report/style'
import {
  AnalysisFollowupResult,
  getAnalysisFollowupResult,
} from 'api/analysisApi'
import {INIT_BRAIN_CONNECTIVITY_ANALYSIS_REPORT} from 'pages/Analysis/EEGSummary/constants'
import useLanguageReport from 'hooks/useLanguageReport'
import {ResHealthReport} from 'api/languageApi'
import {SCORE_HEALTH_REDUCE} from 'constants/HealthCenterConstant'

interface IBrainFunctionConnectivityProps {
  textHealthAPI: ResHealthReport | null
}

const BrainFunctionConnectivity = ({
  textHealthAPI,
}: IBrainFunctionConnectivityProps) => {
  const [dataAnalysisFollowupResult, setDataAnalysisFollowupResult] =
    useState<AnalysisFollowupResult>(INIT_BRAIN_CONNECTIVITY_ANALYSIS_REPORT)

  const {t, i18n} = useTranslation()
  const {textHeaderAPI} = useLanguage('eeg')

  function normalizeScore(score: number): number {
    if (score < SCORE_HEALTH_REDUCE.low) return SCORE_HEALTH_REDUCE.low
    if (score > SCORE_HEALTH_REDUCE.high) return SCORE_HEALTH_REDUCE.high
    return score
  }

  function normalizeBrainConnectivityBetweenLobesScore(
    score: {
      vertical: {left: number; right: number}
      horizontal: number
    } | null,
  ) {
    if (!score) return null

    return {
      vertical: {
        left: normalizeScore(score.vertical.left),
        right: normalizeScore(score.vertical.right),
      },
      horizontal: normalizeScore(score.horizontal),
    }
  }

  function normalizeBrainConnectivityByLobeScore(
    score: {
      frontal: {left: number; right: number}
      pariocci: {left: number; right: number}
    } | null,
  ) {
    if (!score) return null

    return {
      frontal: {
        left: normalizeScore(score.frontal.left),
        right: normalizeScore(score.frontal.right),
      },
      pariocci: {
        left: normalizeScore(score.pariocci.left),
        right: normalizeScore(score.pariocci.right),
      },
    }
  }

  const {
    params: {id},
  } = useRouteMatch<{id: string}>()
  const classes = useStyles()

  const {itemsBrainFunction, getReportBrainFunction} = useEegSummaryReport()

  const [patient, setPatient] = useState<PatientDetail | null>(null)

  const getAnalysisFollowupResultApi = async () => {
    const response = await getAnalysisFollowupResult(Number(id), i18n.language)
    setDataAnalysisFollowupResult(response)
  }

  useEffect(() => {
    const fetchData = async () => {
      if (itemsBrainFunction?.result && dataAnalysisFollowupResult) {
        const user = await fetchPatientApi({
          uuid: itemsBrainFunction.patientUuid,
        })
        if (user?.data) {
          setPatient(user.data)
        }
      }
    }
    fetchData()
  }, [itemsBrainFunction, dataAnalysisFollowupResult])
  const {isLocalLanguage} = useLanguageReport('health')

  useEffect(() => {
    const param = {
      id: Number.parseInt(id, 10),
      payload: {
        lang: i18n.language,
      },
    }
    getReportBrainFunction(param)
    getAnalysisFollowupResultApi()
  }, [i18n.language])

  return (
    <>
      <HeaderV1
        title={textHealthAPI?.brain_connectivity.title ?? ''}
        content={textHealthAPI?.header.subject_info_guide ?? ''}
        name={t('IGetFullName', {
          firstName: itemsBrainFunction?.patientFirstName,
          lastName: itemsBrainFunction?.patientLastName,
        })}
        sex={
          patient?.gender
            ? t(toGenderString(patient?.gender as GenderValue))
            : ''
        }
        birthday={patient?.birth ?? ''}
        measurementDate={String(itemsBrainFunction?.measureDate)}
        textHeader={textHeaderAPI}
        isLocalLanguage={isLocalLanguage}
        eceo=''
        isShowEceo={false}
      />
      {itemsBrainFunction?.result ? (
        <>
          {textHealthAPI?.brain_connectivity && (
            <BrainFunctionalConnectivityV1
              partOneImg={
                dataAnalysisFollowupResult.images.total_connection ?? ''
              }
              brainConnectivityTotalScore={normalizeScore(
                dataAnalysisFollowupResult.scores.total ?? 0,
              )}
              brainConnectivityBetweenLobesScore={normalizeBrainConnectivityBetweenLobesScore(
                dataAnalysisFollowupResult.scores.inter ?? null,
              )}
              brainConnectivityByLobeScore={normalizeBrainConnectivityByLobeScore(
                dataAnalysisFollowupResult.scores.intra ?? null,
              )}
              textBrainConnectivity={textHealthAPI?.brain_connectivity}
              scoreAnnotation={
                textHealthAPI?.common?.score_annotation?.t_score ?? ''
              }
              pointUnit={textHealthAPI?.common?.score_unit?.t_score ?? ''}
              footer={textHealthAPI?.common?.footer?.[0] ?? ''}
            />
          )}
        </>
      ) : (
        <div className={classes.hrvError}>
          {t('IHealthReportTabNull', {tab: t('IBrainConnectivity')})}
        </div>
      )}
    </>
  )
}
export default BrainFunctionConnectivity
